import { forwardRef, useId, type InputHTMLAttributes } from "react";
import { PhoneCountrySelect, usePhoneCountries } from "../../auth/PhoneCountrySelect";
import { useControlHost } from "./useControlHost";
import { useLocale } from "../../i18n";
import "./phone-input.css";

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  validationError?: string;
  validate?: boolean;
  countryCode: string;
  onCountryChange: (country: string) => void;
};

/** One phone entry pattern for sign-in, account lookup and delivery simulation. */
export const AppPhoneInput = forwardRef<HTMLInputElement, Props>(function AppPhoneInput(
  { countryCode, onCountryChange, disabled, className, validationError, validate = true, ...input }, ref,
) {
  const id = useId();
  const { w } = useLocale();
  const { countries, failed, retry } = usePhoneCountries();
  const host = useControlHost(disabled);
  const phone = String(input.value ?? "");
  const digits = phone.replace(/[^0-9٠-٩۰-۹]/g, "");
  const malformed = !!phone.trim() && (!/^[+()\s0-9٠-٩۰-۹-]+$/.test(phone) || digits.length < 8 || digits.length > 17);
  return <div ref={host.ref} className="app-phone-field" data-disabled={host.disabled || undefined}>
    <div className="app-phone-input" dir="ltr">
      <PhoneCountrySelect countries={countries} value={countryCode} onChange={onCountryChange} disabled={host.disabled} />
      <input {...input} id={input.id ?? id} ref={ref} className={className} type="tel" inputMode="tel" dir="ltr"
        data-validation-error={validationError || (validate && malformed ? w("أدخل رقم هاتف كاملًا وصحيحًا.", "Enter a complete, valid phone number.") : undefined)}
        autoComplete={input.autoComplete ?? "tel"} maxLength={input.maxLength ?? 32}
        placeholder={input.placeholder ?? (countryCode === "JO" ? "07X XXX XXXX" : undefined)} disabled={host.disabled} />
    </div>
    {failed && <div className="app-phone-recovery">
      <span role="status">{w("تعذّر تحميل قائمة الدول.", "Could not load the country list.")}</span>
      <button type="button" disabled={host.disabled} onClick={retry}>{w("إعادة المحاولة", "Try again")}</button>
    </div>}
  </div>;
});
